import React, { useEffect, useMemo, useState, useRef } from "react";
import { Button, Select, Table, Tag, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { allapiAction } from "../../Redux/common/action";
import moment from "moment";
import { toast } from "react-toastify";
import { GetUser, Logout } from "../utils";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function LeafletMap({ lat, lng, name, address }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    mapInstance.current = L.map(mapRef.current).setView([lat, lng], 15);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(mapInstance.current);

    L.marker([lat, lng])
      .addTo(mapInstance.current)
      .bindPopup(`<strong>${name}</strong><br/>${address}`)
      .openPopup();

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [lat, lng, name, address]);

  return <div ref={mapRef} style={{ height: "100%", width: "100%" }} />;
}

function Orders() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pagev = searchParams.get("page");
  const page = pagev == null ? 1 : pagev;

  const user = GetUser();

  const assigned_order_list = useSelector((state) =>
    state?.allapi?.assigned_order_list ? state?.allapi?.assigned_order_list : {}
  );
  const loading = useSelector(
    (state) => (state?.allapi?.loading ? state?.allapi?.loading : false)
  );

  const [statusFilter, setStatusFilter] = useState("");
  const [mapModal, setMapModal] = useState({ open: false, order: null });
  const [otpModal, setOtpModal] = useState({ open: false, order: null });
  const [otpValue, setOtpValue] = useState("");

  const listdata = assigned_order_list?.data?.docs
    ? assigned_order_list?.data?.docs
    : [];
  const summary = assigned_order_list?.summary
    ? assigned_order_list?.summary
    : {};

  useEffect(() => {
    dispatch(allapiAction.getAssignedOrderList(page));
    return () => {};
  }, [location]);

  const filterdData = useMemo(() => {
    if (!statusFilter) return listdata;
    return listdata.filter((item) => item?.status == statusFilter);
  }, [listdata, statusFilter]);

  const totalAssigned = summary?.totalAssigned ? summary?.totalAssigned : 0;
  const totalDelivered = summary?.totalDelivered ? summary?.totalDelivered : 0;
  const totalAccepted = summary?.totalAccepted ? summary?.totalAccepted : 0;
  const totalOutForDelivery = summary?.totalOutForDelivery ? summary?.totalOutForDelivery : 0;

  const handleUpdateStatus = (item, status, otp) => {
    const payload = {
      _id: item?._id,
      status: status,
      page: page,
    };
    if (otp) {
      payload.otp = otp;
    }
    dispatch(allapiAction.updateAssignmentStatus(payload));
  };

  const handleOpenOtpModal = (item) => {
    setOtpValue("");
    setOtpModal({ open: true, order: item });
  };

  const handleCloseOtpModal = () => {
    setOtpModal({ open: false, order: null });
    setOtpValue("");
  };

  const handleConfirmOtp = () => {
    if (!otpValue || otpValue.trim().length !== 6) {
      toast.error("Please enter 6 digit OTP");
      return;
    }
    handleUpdateStatus(otpModal.order, "Delivered", otpValue.trim());
    handleCloseOtpModal();
  };

  const handleLogout = () => {
    Logout();
    navigate("/login");
  };

  const handleOpenMap = (item) => {
    setMapModal({ open: true, order: item });
  };

  const handleCloseMap = () => {
    setMapModal({ open: false, order: null });
  };

  const getUserLocation = (item) => {
    const loc = item?.orderId?.user_id?.location;
    if (loc?.coordinates?.length === 2) {
      return { lat: loc.coordinates[0], lng: loc.coordinates[1] };
    }
    return null;
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      Assigned: { color: "blue", text: "Assigned" },
      Accepted: { color: "orange", text: "Accepted" },
      "Out for Delivery": { color: "purple", text: "Out for Delivery" },
      Delivered: { color: "green", text: "Delivered" },
      Cancelled: { color: "red", text: "Cancelled" },
    };
    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: 60,
      render: (value, item, index) => (page - 1) * 10 + index + 1,
    },
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      width: 140,
      render: (value, item) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
          {item?.orderId?.sellCode}
        </span>
      ),
    },
    {
      title: "Customer",
      dataIndex: "orderId",
      key: "customer",
      width: 180,
      render: (value, item) => (
        <div>
          <strong>{item?.orderId?.user_id?.fullName}</strong>
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
            {item?.orderId?.user_id?.phone}
          </div>
        </div>
      ),
    },
    {
      title: "Products",
      dataIndex: "orderId",
      key: "products",
      width: 200,
      render: (value, item) => {
        const productList = item?.orderId?.orderproductsList
          ? item?.orderId?.orderproductsList
          : [];
        return (
          <div style={{ fontSize: 12 }}>
            {productList?.map((data, i) => (
              <div key={i} style={{ marginBottom: 4 }}>
                <span>{data?.product_names}</span>
                <span style={{ color: "#999", marginLeft: 8 }}>
                  x{data?.itme_count}
                </span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "orderId",
      key: "amount",
      width: 100,
      render: (value, item) => (
        <span style={{ fontWeight: 600 }}>₹ {item?.orderId?.sub_total}</span>
      ),
    },
    {
      title: "Delivery Address",
      dataIndex: "orderId",
      key: "address",
      width: 220,
      render: (value, item) => {
        const address = item?.orderId?.address_id;
        return (
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            {address?.address}
            <br />
            {address?.city}, {address?.state} - {address?.pincode}
          </div>
        );
      },
    },
    {
      title: "Collect From",
      dataIndex: "orderId",
      key: "vendor",
      width: 220,
      render: (value, item) => {
        const vendor = item?.orderId?.vendor_id;
        if (!vendor) return <span style={{ fontSize: 12, color: "#999" }}>-</span>;
        return (
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            <strong>{vendor?.fullName}</strong>
            <div style={{ color: "#666" }}>{vendor?.phone}</div>
            <div style={{ color: "#999", marginTop: 2 }}>
              {vendor?.address1 || vendor?.address2
                ? `${vendor?.address1 || ""}${vendor?.address1 && vendor?.address2 ? ", " : ""}${vendor?.address2 || ""}`
                : ""}
              {vendor?.city ? `, ${vendor?.city}` : ""}
              {vendor?.state ? `, ${vendor?.state}` : ""}
              {vendor?.zip ? ` - ${vendor?.zip}` : ""}
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (value, item) => getStatusTag(item?.status),
    },
    {
      title: "Assigned Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (value, item) => (
        <span style={{ fontSize: 12 }}>
          {moment(item?.createdAt).format("DD/MM/YYYY HH:mm")}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      width: 200,
      render: (value, item) => {
        const status = item?.status;
        const hasLocation = getUserLocation(item);
        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {hasLocation && (
              <Button
                size="small"
                style={{ backgroundColor: "#13c2c2", borderColor: "#13c2c2", color: "#fff" }}
                onClick={() => handleOpenMap(item)}
              >
                Map
              </Button>
            )}
            {status == "Assigned" && (
              <Button
                size="small"
                type="primary"
                onClick={() => handleUpdateStatus(item, "Accepted")}
              >
                Accept
              </Button>
            )}
            {status == "Accepted" && (
              <Button
                size="small"
                onClick={() => handleUpdateStatus(item, "Out for Delivery")}
              >
                Out for Delivery
              </Button>
            )}
            {status == "Out for Delivery" && (
              <Button
                size="small"
                type="primary"
                onClick={() => handleOpenOtpModal(item)}
              >
                Delivered
              </Button>
            )}
            {status == "Delivered" && (
              <Tag color="green">Delivered</Tag>
            )}
            {status == "Cancelled" && <Tag color="red">Cancelled</Tag>}
          </div>
        );
      },
    },
  ];

  const handlePageChange = (e) => {
    navigate("/orders?page=" + e);
  };

  const summaryCards = [
    { title: "Assigned", value: totalAssigned, color: "#096dd9" },
    { title: "Accepted", value: totalAccepted, color: "#fa8c16" },
    { title: "Out for Delivery", value: totalOutForDelivery, color: "#722ed1" },
    { title: "Delivered", value: totalDelivered, color: "#389e0d" },
  ];

  return (
    <div className="orders-page">
      <div className="delivery-header">
        <div className="header-left">
          <h4>My Orders</h4>
          <span className="partner-name">
            {user?.fullName} ({user?.phone})
          </span>
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      <div className="main-container">
        <div className="summary-row mb-10">
          {summaryCards.map((card, i) => (
            <div className="col-md-3 col-6" key={i}>
              <div
                className="summary-card"
                style={{ borderLeft: `4px solid ${card.color}` }}
              >
                <div className="summary-title">{card.title}</div>
                <div className="summary-value" style={{ color: card.color }}>
                  {card.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="filter-bar mb-10">
          <Select
            style={{ width: 200, minWidth: 200 }}
            size="middle"
            placeholder="All Status"
            value={statusFilter || undefined}
            onChange={(val) => setStatusFilter(val)}
            options={[
              { label: "All Status", value: "" },
              { label: "Assigned", value: "Assigned" },
              { label: "Accepted", value: "Accepted" },
              { label: "Out for Delivery", value: "Out for Delivery" },
              { label: "Delivered", value: "Delivered" },
              { label: "Cancelled", value: "Cancelled" },
            ]}
          />
        </div>

        <div className="table-responsive-box">
          <Table
            loading={loading}
            className="orders-table"
            dataSource={filterdData}
            columns={columns}
            rowKey={(record) => record?._id}
            pagination={{
              current: parseInt(page),
              pageSize: 10,
              onChange: handlePageChange,
              hideOnSinglePage: true,
              total: assigned_order_list?.data?.totalDocs
                ? assigned_order_list?.data?.totalDocs
                : 0,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
            scroll={{ x: 1300 }}
          />
        </div>
      </div>

      <Modal
        title={
          <span>
            Customer Location - {mapModal.order?.orderId?.user_id?.fullName}
          </span>
        }
        open={mapModal.open}
        onCancel={handleCloseMap}
        footer={null}
        width={700}
      >
        {getUserLocation(mapModal.order) ? (
          <div>
            <div style={{ marginBottom: 8, fontSize: 13, color: "#666" }}>
              <strong>Address:</strong>{" "}
              {mapModal.order?.orderId?.address_id?.address},{" "}
              {mapModal.order?.orderId?.address_id?.city},{" "}
              {mapModal.order?.orderId?.address_id?.state} -{" "}
              {mapModal.order?.orderId?.address_id?.pincode}
            </div>
            <div style={{ marginBottom: 8, fontSize: 13, color: "#666" }}>
              <strong>Phone:</strong> {mapModal.order?.orderId?.user_id?.phone}
            </div>
            <div style={{ height: 400, width: "100%", borderRadius: 8, overflow: "hidden" }}>
              <LeafletMap
                lat={getUserLocation(mapModal.order).lat}
                lng={getUserLocation(mapModal.order).lng}
                name={mapModal.order?.orderId?.user_id?.fullName}
                address={mapModal.order?.orderId?.address_id?.address}
              />
            </div>
            <div style={{ marginTop: 8, textAlign: "center" }}>
              <a
                href={`https://www.google.com/maps?q=${getUserLocation(mapModal.order).lat},${getUserLocation(mapModal.order).lng}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1890ff" }}
              >
                Open in Google Maps
              </a>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
            Location not available for this customer
          </div>
        )}
      </Modal>

      <Modal
        title={
          <span>
            Enter Delivery OTP
          </span>
        }
        open={otpModal.open}
        onCancel={handleCloseOtpModal}
        onOk={handleConfirmOtp}
        okText="Confirm Delivery"
        cancelText="Cancel"
        width={420}
      >
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔑</div>
          <p style={{ fontSize: 14, color: "#666", marginBottom: 6 }}>
            Ask the customer for their <strong>6 digit OTP</strong>
          </p>
          <p style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>
            Order: <strong>{otpModal.order?.orderId?.sellCode}</strong> •{" "}
            {otpModal.order?.orderId?.user_id?.fullName}
          </p>
          <input
            type="text"
            maxLength={6}
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="Enter 6 digit OTP"
            autoFocus
            style={{
              width: "100%",
              padding: "12px",
              fontSize: 22,
              letterSpacing: 10,
              textAlign: "center",
              fontWeight: 700,
              border: "2px solid #d9d9d9",
              borderRadius: 8,
              outline: "none",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#1890ff")}
            onBlur={(e) => (e.target.style.borderColor = "#d9d9d9")}
          />
        </div>
      </Modal>
    </div>
  );
}

export default Orders;